// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A table component for displaying purchase order information with filtering.
 */
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { useTableSort } from '@/hooks/use-table-sort';
import { getFilterStatuses } from '@/purchase-order/api/get-purchase-orders';
import { PurchaseOrderTableRow } from '@/purchase-order/components/purchase-order-table-row';
import { PurchaseOrderFilter } from '@/purchase-order/constants/status';
import {
  PurchaseOrderSortProperty,
  usePurchaseOrders,
} from '@/purchase-order/hooks/use-purchase-orders';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useCallback, useEffect } from 'react';

/**
 * Props for the PurchaseOrderTable component.
 */
interface Props {
  filter?: PurchaseOrderFilter;
  sort: Sort<PurchaseOrderSortProperty>;
  onSort: (property: PurchaseOrderSortProperty) => void;
  onPageChange: (pageNumber: number) => void;
}

/**
 * A table component for displaying purchase order information with filtering.
 */
export function PurchaseOrderTable(props: Props) {
  const { purchaseOrders, resultCount } = usePurchaseOrders({
    filter: props.filter,
  });

  const pageNumber = props.filter?.page ?? 1;
  const pageSize = props.filter?.pageSize ?? 20;

  const onPageChange = props.onPageChange;

  const downloadURL = new URL(
    '/v1/export/purchase-orders',
    import.meta.env.VITE_API_URL
  );
  const statuses = getFilterStatuses(
    props.filter?.status,
    props.filter?.subStatuses
  );
  if (statuses) {
    for (const s of statuses) {
      downloadURL.searchParams.append('statuses', s);
    }
  }
  if (props.filter?.searchTerm) {
    downloadURL.searchParams.set('search', props.filter.searchTerm);
  }

  /**
   * Table sort helpers for managing sort indicators.
   */
  const { getSortDirection, getLabelDirection, isLabelActive } = useTableSort(
    props.sort
  );

  /**
   * Creates a sort handler for a specific column.
   */
  const createSortHandler = useCallback(
    (property: PurchaseOrderSortProperty) => () => {
      props.onSort(property);
    },
    [props]
  );

  // Handle page overrun
  useEffect(() => {
    if (onPageChange && purchaseOrders.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [purchaseOrders.length, pageNumber, onPageChange]);

  if (purchaseOrders.length === 0) {
    return (
      <EmptyState title="No purchase orders found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            To start tracking purchase orders in Hardfin, add a new purchase
            order
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                width="25%"
                sortDirection={getSortDirection('poNumber')}
              >
                <TableSortLabel
                  active={isLabelActive('poNumber')}
                  direction={getLabelDirection('poNumber')}
                  onClick={createSortHandler('poNumber')}
                >
                  References
                </TableSortLabel>
              </TableCell>
              <TableCell width="25%">From/To location</TableCell>
              <TableCell width="30%">Items</TableCell>
              <TableCell width="20%" sortDirection={getSortDirection('status')}>
                <TableSortLabel
                  active={isLabelActive('status')}
                  direction={getLabelDirection('status')}
                  onClick={createSortHandler('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchaseOrders.map((purchaseOrder) => (
              <PurchaseOrderTableRow
                key={purchaseOrder.id}
                purchaseOrder={purchaseOrder}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize,
          onPageChange,
        }}
        totalCount={resultCount}
        entityOptions={{ singular: 'purchase order', includeCount: false }}
        downloadURL={downloadURL}
        downloadLabel="Export purchase order data"
      />
    </>
  );
}
